import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { useAuth } from "../lib/auth";
import Logo from "./Logo";
import ImpersonationBanner from "./ImpersonationBanner";
import {
  Gauge, Users, Buildings, Bank, MapTrifold, Briefcase, ListChecks,
  Calendar, Leaf, MagnifyingGlass, Bell, SignOut, Compass, UsersThree,
  Buildings as BuildingsAdmin, Target, Scales, CaretDown, FolderOpen, UserCircle,
  ShieldCheck,
} from "@phosphor-icons/react";

// Display labels for user roles in the header badge
const ROLE_LABELS = {
  super_admin: "Super Admin",
  admin_empresa: "Empresa",
  gerente: "Gerente",
  encargado_social: "Encargado Social",
  encargado_ambiental: "Encargado Ambiental",
  encargado_proyecto: "Encargado Proyecto",
  consulta: "Consulta",
  auditor: "Auditor",
};

// Map organization industry to a friendly badge label
const INDUSTRY_LABELS = {
  municipio: "Municipio",
  municipalidad: "Municipio",
  gobierno: "Gobierno",
  gobierno_regional: "Gobierno Regional",
  gore: "Gobierno Regional",
  publico: "Sector Público",
  ong: "ONG",
  fundacion: "Fundación",
  cooperativa: "Cooperativa",
  mineria: "Minería",
  empresa: "Empresa",
  privado: "Empresa",
};

function orgTypeLabel(industry) {
  if (!industry) return null;
  const key = String(industry).trim().toLowerCase();
  return INDUSTRY_LABELS[key] || industry;
}

// Top-level menu structure (groups + standalone items)
const navTree = [
  { kind: "item", to: "/dashboard", label: "Dashboard", icon: Gauge },
  { kind: "item", to: "/comunidades", label: "Comunidades", icon: Users },
  {
    kind: "group", id: "autoridades", label: "Autoridades", icon: Bank,
    children: [
      { to: "/autoridades-comunales", label: "Aut. Comunales", icon: Bank },
      { to: "/autoridades-gobierno", label: "Aut. Gobierno", icon: Buildings },
      { to: "/parlamentarios", label: "Parlamentarios", icon: Scales },
      { to: "/autoridades-gore", label: "Aut. GORE", icon: Compass },
      { to: "/organizaciones", label: "Organizaciones", icon: Briefcase },
    ],
  },
  {
    kind: "group", id: "gestion", label: "Gestión", icon: FolderOpen,
    children: [
      { to: "/compromisos", label: "Compromisos", icon: ListChecks },
      { to: "/proyectos", label: "Proyectos", icon: Briefcase },
      { to: "/metas", label: "Metas / Asuntos Públicos", icon: Target },
      { to: "/reuniones", label: "Reuniones", icon: Calendar },
      { to: "/agenda", label: "Agenda", icon: Calendar },
    ],
  },
  { kind: "item", to: "/rca", label: "RCA", icon: Leaf },
  { kind: "item", to: "/mapa", label: "Mapa", icon: MapTrifold },
  { kind: "item", to: "/equipo", label: "Equipo", icon: UsersThree },
  { kind: "item", to: "/usuarios", label: "Usuarios y accesos", icon: ShieldCheck, requiresManageUsers: true },
  { kind: "item", to: "/buscador", label: "Buscador", icon: MagnifyingGlass },
  { kind: "item", to: "/alertas", label: "Alertas", icon: Bell },
];


function SidebarGroup({ group, currentPath }) {
  const hasActiveChild = group.children.some((c) => currentPath.startsWith(c.to));
  const [open, setOpen] = useState(hasActiveChild);

  // Auto-expand whenever a child becomes active (e.g., after navigation)
  useEffect(() => {
    if (hasActiveChild) setOpen(true);
  }, [hasActiveChild]);

  return (
    <div data-testid={`nav-group-${group.id}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid={`nav-group-toggle-${group.id}`}
        className={`sidebar-link w-full justify-between ${hasActiveChild ? "active" : ""}`}
        aria-expanded={open}
      >
        <span className="flex items-center gap-2">
          <group.icon size={18} weight="duotone" className={hasActiveChild ? "text-white" : "text-brand-orange-400"} />
          <span>{group.label}</span>
        </span>
        <CaretDown size={14} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="ml-3 mt-1 mb-1 space-y-1 border-l border-slate-700 pl-2">
          {group.children.map((c) => (
            <NavLink
              key={c.to}
              to={c.to}
              data-testid={`nav-${c.to.replace(/\//g, "")}`}
              className={({ isActive }) => `sidebar-link sidebar-link-sub ${isActive ? "active" : ""}`}
            >
              {({ isActive }) => (
                <>
                  <c.icon size={15} weight="duotone" className={isActive ? "text-white" : "text-brand-orange-400"} />
                  <span>{c.label}</span>
                </>
              )}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}


export default function AppLayout() {
  const { user, logout, impersonating } = useAuth();
  const nav = useNavigate();
  const { pathname } = useLocation();

  return (
    <div className="flex h-screen w-full overflow-hidden bg-slate-50">
      {/* Sidebar */}
      <aside
        className="hidden md:flex w-64 flex-col bg-slate-900 text-slate-200"
        data-testid="app-sidebar"
      >
        <div className="px-3 py-3 border-b border-slate-800 flex flex-col items-center text-center">
          <img src="/assets/sigsaop-logo-light.svg?v=5" alt="SIGSAOP" className="w-full max-w-[220px] h-auto object-contain" data-testid="sidebar-logo" />
          {impersonating && (
            <div className="text-[10px] uppercase tracking-[0.18em] text-slate-400 mt-2">
              {impersonating.name}
            </div>
          )}
        </div>
        <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
          {user?.role === "super_admin" && (
            <NavLink
              to="/admin/clientes"
              data-testid="nav-admin-clientes"
              className="sidebar-link mb-2 bg-slate-800/60 hover:bg-slate-800"
            >
              <BuildingsAdmin size={18} weight="regular" />
              <span>Panel Super Admin</span>
            </NavLink>
          )}
          {navTree.map((entry) => {
            if (entry.requiresManageUsers) {
              const canManage = ["super_admin", "admin_empresa", "gerente"].includes(user?.role);
              if (!canManage) return null;
            }
            if (entry.kind === "group") {
              return <SidebarGroup key={entry.id} group={entry} currentPath={pathname} />;
            }
            return (
              <NavLink
                key={entry.to}
                to={entry.to}
                data-testid={`nav-${entry.to.replace(/\//g, "")}`}
                className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`}
              >
                {({ isActive }) => (
                  <>
                    <entry.icon size={18} weight="duotone" className={isActive ? "text-white" : "text-brand-orange-400"} />
                    <span>{entry.label}</span>
                  </>
                )}
              </NavLink>
            );
          })}
        </nav>
        <div className="border-t border-slate-800 p-4 text-xs text-slate-400">
          <div className="text-slate-200 font-medium truncate">{user?.full_name}</div>
          <div className="truncate">{user?.email}</div>
        </div>
      </aside>

      {/* Main column */}
      <div className="flex flex-1 flex-col min-w-0">
        <ImpersonationBanner />
        <header
          className="h-16 sticky top-0 z-20 flex items-center justify-between gap-4 border-b border-slate-200 bg-white/80 backdrop-blur-xl px-5"
          data-testid="app-header"
        >
          <div className="flex items-center gap-2 md:hidden">
            <img src="/assets/sigsaop-logo.svg?v=5" alt="SIGSAOP" className="h-8 w-auto object-contain" />
          </div>
          <div className="flex-1" />
          <div className="flex items-center gap-3">
            {user?.role && (
              (() => {
                // For super_admin impersonating a client, show that client's industry.
                // For regular users, show their own org industry. Fallback to role.
                const industrySource = user.role === "super_admin"
                  ? impersonating?.industry
                  : user.org_industry;
                const orgLabel = orgTypeLabel(industrySource);
                const roleLabel = ROLE_LABELS[user.role] || user.role;
                const label = orgLabel || roleLabel;
                return (
                  <span
                    data-testid="header-role-badge"
                    className="hidden sm:inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-700 border border-slate-200"
                    title={`Rol: ${roleLabel}${orgLabel ? " · " + orgLabel : ""}`}
                  >
                    <UserCircle size={14} className="text-slate-500" />
                    {label}
                  </span>
                );
              })()
            )}
            <button
              onClick={() => nav("/alertas")}
              data-testid="header-alerts-btn"
              className="relative rounded-md p-2 text-slate-600 hover:bg-slate-100"
              title="Alertas"
            >
              <Bell size={18} />
            </button>
            <button
              onClick={() => { logout(); nav("/login"); }}
              data-testid="header-logout-btn"
              className="flex items-center gap-2 rounded-md px-3 py-2 text-sm text-slate-600 hover:bg-slate-100"
            >
              <SignOut size={16} />
              <span className="hidden sm:inline">Cerrar sesión</span>
            </button>
          </div>
        </header>
        <main className="flex-1 overflow-y-auto" data-testid="app-main">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
